import React, { useState, useEffect } from "react";
import * as msal from '@azure/msal-browser';
import { FullScreenLoadingSpinner } from "../LoadingSpinner";
import { ChoiceGroup, PrimaryButton, IChoiceGroupOption, MessageBar, MessageBarType } from '@fluentui/react';
import ErrorMessages from "../../../error_messages.json";

const getErrorCodeFromOfficeError = (e: Error) => e.message.split(":")[0];

interface AuthorizationContainerProps {
  children: React.FC<{ token: string }>
}


const msalConfig = {
  auth: {
    clientId: 'OFFICE_365_ENTRA_ID',
    redirectUri: '/authorize',
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case msal.LogLevel.Error:
            console.error(message);
            return;
          case msal.LogLevel.Info:
            console.info(message);
            return;
          case msal.LogLevel.Verbose:
            console.debug(message);
            return;
          case msal.LogLevel.Warning:
            console.warn(message);
            return;
        }
      },
    },
  },
};


export const AuthorizationContainer = ({ children }: AuthorizationContainerProps) => {
  const [token, setToken] = useState<undefined | string>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [blockInteraction, setBlockInteraction] = useState(false);

  const loginRequest = {
    scopes: ["Mail.Read", "Mail.Send"],
    domainHint: Office.context.mailbox.userProfile.emailAddress.split("@")[1]
  };

  const replicateSessionStorage = (target: "localStorage" | "sessionStorage") => {
    const ENABLE_LOCAL_STORAGE_COPY = Office.context.platform == "PC"
    if (!ENABLE_LOCAL_STORAGE_COPY) return;
    console.log("LEGACY OFFICE ADDIN DETECTED")
    if (target == "localStorage") {
      Object.entries(sessionStorage).forEach(([key, value]) => localStorage.setItem(key, value))
    } else {
      Object.entries(localStorage).forEach(([key, value]) => sessionStorage.setItem(key, value))
    }
  }

  useEffect(() => {
    replicateSessionStorage("sessionStorage")
    const myMSALObj = new msal.PublicClientApplication(msalConfig);
    myMSALObj.initialize()
      .then(() => myMSALObj.acquireTokenSilent(loginRequest))
      .then((tokenResponse) => {
        setToken(tokenResponse.accessToken);
        setLoading(false);
        replicateSessionStorage("localStorage")
      }).catch((e) => {
        console.log(e)
        setLoading(false);
      })
  }, [])

  const authorizeClient = () => {
    const myMSALObj = new msal.PublicClientApplication(msalConfig);
    myMSALObj.initialize()
      .then(() => setLoading(true))
      .then(() => myMSALObj.acquireTokenPopup(loginRequest))
      .then((tokenResponse) => {
        myMSALObj.setActiveAccount(tokenResponse.account);
        setToken(tokenResponse.accessToken);
        setLoading(false);
        replicateSessionStorage("localStorage")
      }).catch((e) => {
        const code = getErrorCodeFromOfficeError(e);

        if (ErrorMessages[code]) {
          const { message, block = false } = ErrorMessages[code];
          setError(message);
          setBlockInteraction(block);
          setLoading(false);
          return
        }

        setError(e.message);
        setBlockInteraction(false);
        setLoading(false);
      })
  }

  if (loading) {
    return <FullScreenLoadingSpinner />
  }

  if (!token) {

    return <>
      <MessageBar messageBarType={MessageBarType.warning}>This app requires authorization before use</MessageBar>
      {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
      <div style={{ position: "relative" }}>
        <div style={{
          opacity: 0.3,
          pointerEvents: "none"
        }}>
          {children({ token: "" })}
        </div>
        <div style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          top: "0px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}>
          <PrimaryButton disabled={blockInteraction} onClick={() => authorizeClient()}>Authorize</PrimaryButton>
        </div>
      </div>
    </>
  }

  return <>
    {children({ token })}
  </>
}
